import React, { useEffect } from 'react';
import { ConfigProvider, ConfigProviderProps } from 'antd';
import enUS from 'antd/locale/en_US';
import frFR from 'antd/locale/fr_FR';
import jaJP from 'antd/locale/ja_JP';
import koKR from 'antd/locale/ko_KR';
// antd locale
import zhCN from 'antd/locale/zh_CN';
// 时间相关
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Helmet } from 'react-helmet';

// dayjs locale
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/ja';
import 'dayjs/locale/ko';
// i18n
import '@/locales/i18n';

import { useTranslation } from 'react-i18next';

import { LanguageEnum } from '@/locales/config';

import './global.less';

import { Router } from '@/router';
import DingTalkLogin from '@/service/ding-talk-login';
import { THEME_COLORS } from '@/theme/colors';
import { overrideHistoryBack } from '@/utils/dom';

dayjs.extend(utc);
dayjs.extend(timezone);

ConfigProvider.config({
  holderRender(children) {
    return (
      <ConfigProvider locale={zhCN} prefixCls="antd">
        {children}
      </ConfigProvider>
    );
  },
});

overrideHistoryBack();

// 初始化登录服务
DingTalkLogin.init();

/**
 * AI.GEN lines=1-20
 * App 组件 - 初始化登录服务并集成 i18n
 */
export const App = () => {
  const { i18n, t } = useTranslation();

  // 根据当前语言动态设置 antd locale 和 dayjs locale
  const getAntdLocale = () => {
    const language = i18n.language;
    switch (language) {
      case LanguageEnum.ZhCN:
        return zhCN;
      case LanguageEnum.EnUS:
        return enUS;
      case LanguageEnum.FrFR:
        return frFR;
      case LanguageEnum.JaJP:
        return jaJP;
      case LanguageEnum.KoKR:
        return koKR;
      default:
        return zhCN;
    }
  };

  // 设置 dayjs locale
  useEffect(() => {
    const language = i18n.language;
    let dayjsLocale = 'zh-cn';
    switch (language) {
      case LanguageEnum.ZhCN:
        dayjsLocale = 'zh-cn';
        break;
      case LanguageEnum.EnUS:
        dayjsLocale = 'en';
        break;
      case LanguageEnum.FrFR:
        dayjsLocale = 'fr';
        break;
      case LanguageEnum.JaJP:
        dayjsLocale = 'ja';
        break;
      case LanguageEnum.KoKR:
        dayjsLocale = 'ko';
        break;
    }
    dayjs.locale(dayjsLocale);
  }, [i18n.language]);

  const antdConfig: ConfigProviderProps = {
    locale: getAntdLocale(),
    prefixCls: 'antd',
    input: {
      allowClear: true,
    },
    textArea: {
      allowClear: true,
    },
    /**
     * AI.GEN lines=1-50
     * antd主题配置：使用颜色变量统一管理
     */
    theme: {
      token: {
        fontSize: 14,
        colorPrimary: THEME_COLORS.primary,
        colorPrimaryHover: THEME_COLORS.focusBorder, // 全局hover颜色
        colorTextLightSolid: THEME_COLORS.textPrimary, // 主色背景上的文字颜色
        // 禁用蓝色：链接/图标/信息色统一为#1a1a1a
        colorLink: '#1a1a1a',
        colorLinkHover: '#1a1a1a',
        colorLinkActive: '#1a1a1a',
        colorIcon: '#1a1a1a',
        colorIconHover: '#1a1a1a',
        colorInfo: '#1a1a1a',
        colorInfoHover: '#1a1a1a',
        colorInfoActive: '#1a1a1a',
        colorInfoBorder: '#d9d9d9',
        colorInfoBorderHover: '#1a1a1a',
        colorPrimaryText: '#1a1a1a',
        colorPrimaryTextHover: '#1a1a1a',
        colorPrimaryTextActive: '#1a1a1a',
      },
      components: {
        Breadcrumb: {
          itemColor: THEME_COLORS.white,
          lastItemColor: THEME_COLORS.white,
          separatorColor: THEME_COLORS.white,
        },
        Button: {
          colorText: THEME_COLORS.textPrimary,
          colorTextLightSolid: THEME_COLORS.textPrimary,
          colorPrimaryHover: THEME_COLORS.primaryHover, // primary按钮hover亮黄色高亮
          defaultHoverBorderColor: '#999999',
          defaultHoverColor: '#1A1A1A',
        },
        Checkbox: {
          colorPrimaryHover: THEME_COLORS.primaryHover,
          colorText: THEME_COLORS.textPrimary,
          colorTextDisabled: THEME_COLORS.textDisabled,
        },
        Switch: {
          colorPrimaryHover: THEME_COLORS.primaryHover,
        },
        Tabs: {
          itemColor: THEME_COLORS.textPrimary,
          itemSelectedColor: THEME_COLORS.textPrimary,
          itemHoverColor: THEME_COLORS.textPrimary,
        },
        Input: {
          activeBorderColor: THEME_COLORS.focusBorder,
          hoverBorderColor: THEME_COLORS.focusBorder,
        },
        InputNumber: {
          activeBorderColor: THEME_COLORS.focusBorder,
          hoverBorderColor: THEME_COLORS.focusBorder,
        },
        Select: {
          optionSelectedColor: THEME_COLORS.textPrimary,
          optionSelectedBg: THEME_COLORS.bgLight,
          colorBorder: THEME_COLORS.border,
          activeBorderColor: THEME_COLORS.focusBorder,
          hoverBorderColor: THEME_COLORS.focusBorder,
        },
        DatePicker: {
          cellActiveWithRangeBg: THEME_COLORS.rangeBg,
          activeBorderColor: THEME_COLORS.focusBorder,
          hoverBorderColor: THEME_COLORS.focusBorder,
        },
        Tooltip: {
          colorBgSpotlight: THEME_COLORS.white,
          colorTextLightSolid: THEME_COLORS.textPrimary,
        },
        Table: {
          headerSortActiveBg: THEME_COLORS.bgLighter,
          headerSortHoverBg: THEME_COLORS.bgLight,
        },
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>{t('app.title')}</title>
      </Helmet>
      <ConfigProvider {...antdConfig}>
        <Router></Router>
      </ConfigProvider>
    </>
  );
};
